import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { SummerDiscovery } from "./components/SummerDiscovery";
import { About } from "./components/About";
import { FounderVideo } from "./components/FounderVideo";
import { BoardMembers } from "./components/BoardMembers";
import { Programs } from "./components/Programs";
import { Gallery } from "./components/Gallery";
import { Events } from "./components/Events";
import { Partners } from "./components/Partners";
import { Sponsors } from "./components/Sponsors";
import { GetInvolved } from "./components/GetInvolved";
import { Footer } from "./components/Footer";
import { AuthProvider } from "./contexts/AuthContext";
import { ContentProvider, useAllContent } from "./contexts/ContentContext";
// Admin-only routes are code-split so the public landing page bundle stays small.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Privacy = lazy(() => import("./pages/Privacy"));

const RouteFallback = () => (
  <div className="min-h-screen grid place-items-center bg-slate-50">
    <div className="w-10 h-10 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Cookies are sent only on auth-required requests (where withCredentials is set
// explicitly). Keeping the default OFF means public endpoints work fine cross-origin
// even when CORS uses a wildcard, which is needed when the site is loaded from a
// custom domain that differs from the API host (e.g. semajmorgan.org calling
// opportunity-awaits.emergent.host).

const Landing = () => {
  const { content } = useAllContent();

  // Bidirectional fade: tiles fade in when in view and gracefully fade out
  // when scrolled away. Re-runs whenever CMS content changes so late-rendered
  // tiles (gallery, etc.) get observed.
  useEffect(() => {
    if (!content) return;
    // Wait for the next paint so the new DOM nodes exist.
    const id = window.requestAnimationFrame(() => {
      const els = document.querySelectorAll("[data-fade]");
      if (!els.length) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
            } else {
              entry.target.classList.remove("is-visible");
            }
          });
        },
        { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
      );
      els.forEach((el) => io.observe(el));
      // Stash so cleanup can disconnect
      window.__sjmFadeObserver?.disconnect?.();
      window.__sjmFadeObserver = io;
    });
    return () => {
      window.cancelAnimationFrame(id);
      window.__sjmFadeObserver?.disconnect?.();
      window.__sjmFadeObserver = null;
    };
  }, [content]);

  return (
    <div className="App" data-testid="app-root">
      <Header />
      <main>
        <Hero />
        <SummerDiscovery />
        <About />
        <FounderVideo />
        <BoardMembers />
        <Programs />
        <Gallery />
        <Events />
        <Partners />
        <Sponsors />
        <GetInvolved />
      </main>
      <Footer />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Detect session_id in URL fragment synchronously (before useEffect) to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <Suspense fallback={<RouteFallback />}>
            <Dashboard />
          </Suspense>
        }
      />
      <Route
        path="/privacy"
        element={
          <Suspense fallback={<RouteFallback />}>
            <Privacy />
          </Suspense>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ContentProvider>
          <AppRouter />
          <Toaster
            position="top-center"
            richColors
            closeButton
            toastOptions={{ style: { fontFamily: "DM Sans, sans-serif" } }}
          />
        </ContentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
